import React, { useState } from 'react'
import { FormattedMessage } from 'react-intl'

import DeclineQuotationModal from '../decline-quotation-modal/DeclineQuotationModal'
import OwnerCurrentActionFooterContainer from '../OwnerCurrentActionFooterContainer'
import UploadQuotationModal, {
  IUploadQuotationProps,
} from '../upload-quotation-modal/UploadQuotationModal'
import { CompanyInformationClientProposalFragment } from '../../../../../../../lib/graphql'
import Button from '../../../../../../atoms/fields/Button/Button'
import {
  EnvironmentTypeEnum,
  useEnvironmentTypeContext,
} from '../../../../../../contexts'
import { IProposalInteractionsProps } from '../../../../../../organisms/proposal/Proposal'
import PingPongButton from '../../../../../../organisms/proposal/proposal-cta/buttons-by-step/PingPongButton'
import { PingPongButtonTypeEnum } from '../../../../../../organisms/proposal/proposal-cta/utils/ping-pong-button-type.enum.ts'

const AdminCanConfirmQuotation = ({
  clientProposal,
  adminConfirmedHostQuotation,
}: {
  clientProposal: CompanyInformationClientProposalFragment
  adminConfirmedHostQuotation: {
    callback: (args: { clientProposalId: string }) => void
    isLoading: boolean
  }
}) => {
  if (clientProposal.quotationConfirmedAt) {
    return (
      <Button disabled={true}>
        <FormattedMessage
          id='ui.ownerCanReUploadQuotationFooter.button.quoteConfirmed'
          defaultMessage='Devis confirmé - visible par le client'
        />
      </Button>
    )
  }
  if ((clientProposal.clientPricingItems ?? []).length > 0) {
    return (
      <PingPongButton
        dataTestId='confirm-quotation-button'
        label={
          <FormattedMessage
            id='ui.ownerCanReUploadQuotationFooter.confirmQuote'
            defaultMessage='Valider le devis'
          />
        }
        type={PingPongButtonTypeEnum.CONFIRM}
        onClick={() =>
          adminConfirmedHostQuotation.callback({
            clientProposalId: clientProposal._id,
          })
        }
        isLoading={adminConfirmedHostQuotation.isLoading}
      />
    )
  } else {
    return (
      <Button disabled={true}>
        <FormattedMessage
          id='ui.ownerCanReUploadQuotationFooter.confirmQuote'
          defaultMessage='Valider le devis'
        />
      </Button>
    )
  }
}

const OwnerCanReUploadQuotationFooter = ({
  clientProposal,
  uploadQuotationData,
  ownerActions,
}: {
  ownerActions: IProposalInteractionsProps['ownerActions']
  uploadQuotationData?: IUploadQuotationProps
  clientProposal: CompanyInformationClientProposalFragment
}) => {
  const [isOpenLoadQuotation, setIsOpenLoadQuotation] = useState(false)
  const [isOpenDeclineQuotationModal, setIsOpenDeclineQuotationModal] =
    useState(false)
  const environment = useEnvironmentTypeContext()
  const isBackOffice = environment === EnvironmentTypeEnum.BACKOFFICE

  const ownerUploadedQuotation = ownerActions?.ownerUploadedQuotation
  const ownerDeclinedQuotation = ownerActions?.ownerDeclinedQuotation
  const adminConfirmedHostQuotation = ownerActions?.adminConfirmedHostQuotation

  if (!ownerUploadedQuotation || !ownerDeclinedQuotation) {
    return null
  }

  return (
    <OwnerCurrentActionFooterContainer>
      {isBackOffice && adminConfirmedHostQuotation && (
        <AdminCanConfirmQuotation
          clientProposal={clientProposal}
          adminConfirmedHostQuotation={adminConfirmedHostQuotation}
        />
      )}

      <div className='flex gap-2'>
        <PingPongButton
          label={
            <FormattedMessage
              id='ui.ownerCanReUploadQuotationFooter.button.waitingConfirmation'
              defaultMessage='Attente de confirmation'
            />
          }
          type={PingPongButtonTypeEnum.WAITING}
          className='!px-0'
        />
        <PingPongButton
          label={
            <FormattedMessage
              id='ui.ownerCanReUploadQuotationFooter.button.uploadAnotherQuotation'
              defaultMessage='Charger un autre devis'
            />
          }
          onClick={() => setIsOpenLoadQuotation(true)}
          type={PingPongButtonTypeEnum.MODIFY_QUOTATION}
          className='!px-0'
        />
      </div>
      <PingPongButton
        label={
          <FormattedMessage
            id='ui.ownerCanReUploadQuotationFooter.button.declineBooking'
            defaultMessage='Refuser la demande'
          />
        }
        onClick={() => setIsOpenDeclineQuotationModal(true)}
        type={PingPongButtonTypeEnum.DECLINED_BY_OWNER}
      />
      {isOpenLoadQuotation && (
        <UploadQuotationModal
          isOpenModal={isOpenLoadQuotation}
          setIsOpenModal={setIsOpenLoadQuotation}
          isModalQuotation={true}
          uploadQuotationData={uploadQuotationData}
          clientProposal={clientProposal}
          ownerUploadedQuotation={ownerUploadedQuotation}
        />
      )}
      {isOpenDeclineQuotationModal && (
        <DeclineQuotationModal
          isOpenModal={isOpenDeclineQuotationModal}
          setIsOpenModal={setIsOpenDeclineQuotationModal}
          ownerDeclinedQuotation={ownerDeclinedQuotation}
        />
      )}
    </OwnerCurrentActionFooterContainer>
  )
}

export default OwnerCanReUploadQuotationFooter
